import React from "react";
import { motion } from "framer-motion";
import { LockIcon, ZapIcon, ShieldCheckIcon, SmartphoneIcon, ArrowRightIcon } from "lucide-react";
import { trackMetaEvent } from "../analytics/metaPixel";
import { trustBadges } from "./data";
export const CHECKOUT_URL = "https://bibliotecatdahyautismoinfantil.impultienda.ar/checkout";
export function scrollToOffer() {
  trackMetaEvent("Lead", {
    content_name: "Pequeños Pasos, Grandes Logros",
    content_category: "Guía digital"
  });
  const el = document.getElementById("oferta");
  if (el) el.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
}
export function goToCheckout() {
  trackMetaEvent("InitiateCheckout", {
    content_name: "Pequeños Pasos, Grandes Logros",
    content_category: "Guía digital",
    currency: "ARS",
    value: 21990
  });
  window.open(CHECKOUT_URL, "_blank", "noopener,noreferrer");
}
const badgeIcon: Record<string, React.ReactNode> = {
  lock: <LockIcon className="h-4 w-4" aria-hidden data-id="element-305" data-mp="a1wnn3z93ng3" />,
  zap: <ZapIcon className="h-4 w-4" aria-hidden data-id="element-306" data-mp="x4ywpcad8x0s" />,
  shield: <ShieldCheckIcon className="h-4 w-4" aria-hidden data-id="element-307" data-mp="x69zp9vzm0qu" />,
  devices: <SmartphoneIcon className="h-4 w-4" aria-hidden data-id="element-308" data-mp="121z3jpf1i3sy" />
};

/** Small trust labels placed under CTAs across the whole page. */
export function TrustBadges({
  className = "",
  compact = false
}: {
  className?: string;
  compact?: boolean;
}) {
  const items = compact ? trustBadges.slice(0, 2) : trustBadges;
  return <ul className={`flex flex-wrap items-center justify-center gap-x-5 gap-y-2 ${className}`} data-id="element-309" data-mp="efivsnurr2ju">
      {items.map(b => <li key={b.label} className="flex items-center gap-1.5 text-xs sm:text-sm text-[var(--muted)]" data-id="element-310" data-mp="nedyvr2inwix">
          <span className="text-[var(--accent)]" data-id="element-311" data-mp="s64f2do4xei9">{badgeIcon[b.icon]}</span>
          {b.label}
        </li>)}
    </ul>;
}

/** Big, calm, reassuring primary CTA. */
export function CTAButton({
  children = "Quiero empezar hoy",
  className = "",
  full = false
}: {
  children?: React.ReactNode;
  className?: string;
  full?: boolean;
}) {
  return <motion.button onClick={scrollToOffer} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} transition={{
    type: "spring",
    stiffness: 400,
    damping: 22
  }} className={`group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--accent)] px-8 py-4 text-base font-extrabold text-white shadow-[0_14px_34px_-10px_rgba(46,114,232,0.65)] hover:bg-[#245dc3] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--accent)]/30 sm:w-auto sm:rounded-full sm:text-lg ${full ? "sm:w-full" : ""} ${className}`} data-id="element-312" data-mp="y0m8nq3d3r2e">
      {children}
      <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden data-id="element-313" data-mp="p068aphx101r" />
    </motion.button>;
}

/** Section wrapper for consistent vertical rhythm + max width. */
export function Section({
  id,
  children,
  className = ""
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <section id={id} className={`w-full px-4 py-10 text-center sm:px-8 sm:py-24 ${className}`} data-id="element-314" data-mp="27xazjz8pnllq">
      <div className="mx-auto w-full max-w-5xl" data-id="element-315" data-mp="2cvcgoemktauk">{children}</div>
    </section>;
}

/** Fade / slide up on scroll into view. */
export function Reveal({
  children,
  delay = 0,
  y = 24,
  className = ""
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-60px"
  }} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className} data-id="element-316" data-mp="2vo6uh58gqwx7">
      {children}
    </motion.div>;
}
export function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]" data-id="element-317" data-mp="39mn3j2tsszii">
      {children}
    </p>;
}